import React, { lazy, Suspense, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { ErrorBoundary } from 'react-error-boundary'
import { FullPageSpinner } from 'src/components/Reusable/FullPageSpinner'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'react-toastify/dist/ReactToastify.css'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useAuth } from '../context/authContext'
import { AppUnauth } from './AppUnauth'
import { usePublicTrustPortalAccount } from './App.hooks'

type ErrorProps = {
  error: Error
}

const ErrorFallback = (props: ErrorProps): JSX.Element => {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{props.error.message}</pre>
    </div>
  )
}

export const App = (): JSX.Element => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ProductChooser />
    </ErrorBoundary>
  )
}

const ProductChooser = () => {
  const { trustAccountId } = usePublicTrustPortalAccount()
  const isVendorPortalRoute = useIsVendorPortalRoute()
  const isVendorAccountChooserRoute = useIsVendorAccountChooserRoute()

  // Checked ahead of everything else below: like the vendor portal and
  // Trust Portal, the account-chooser page is its own standalone, nav-less
  // app (see VendorAccountChooserApp.tsx), identified purely by URL path.
  // It's reached from an unauthenticated invite email link carrying only an
  // opaque selection_token -- there's no account to resolve up front (or at
  // all, until a candidate is chosen), so this check doesn't depend on
  // AuthContext the way AppChooser below does.
  if (isVendorAccountChooserRoute) {
    return <VendorAccountChooser />
  }

  // Checked ahead of the Trust Portal / main-app split below: the vendor
  // portal is its own standalone, nav-less app (see VendorPortalApp.tsx),
  // identified purely by URL path rather than by resolving an account
  // up front like Trust Portal does -- account/session resolution for the
  // vendor portal happens inside VendorPortalApp itself (M1.4's routing
  // decision), not here.
  if (isVendorPortalRoute) {
    return <VendorPortal />
  }

  // Tri-state logic:
  // 1. Loading - fetching trust account id
  // 2. No account id - redirect to main app, we are not in the trust portal
  // 3. Account id - trust account id found, render trust portal
  if (trustAccountId === undefined) {
    return <FullPageSpinner />
  }

  if (trustAccountId) {
    return <TrustPortal />
  }

  return <AppChooser />
}

const TrustPortal = () => {
  const TrustApp = useMemo(() => lazy(() => import('./TrustApp')), [])

  return (
    <Suspense fallback={<></>}>
      <TrustApp />
    </Suspense>
  )
}

// VENDOR_PORTAL_ROUTE_PATTERN matches the vendor-portal's account-scoped URL
// shape, /<accountId>/vendor-portal(/...) (epic AP-19372's URL restructure --
// see VendorPortalQuestionnaire.routes.tsx for the frontend path builders
// this must stay in sync with), as well as the legacy pre-restructure bare
// /vendor-portal shape with no account segment at all -- old invite/resend
// emails already sent before this change link to that bare path, and this
// app still needs to mount VendorPortalApp for it so its landing flow
// (VendorPortalApp.hooks.tsx) can exchange the token and rewrite the URL to
// the new account-scoped shape once the account resolves, rather than the
// link simply dead-ending. Kept as a plain path-prefix check (unlike Trust
// Portal's domain/account-id resolution in usePublicTrustPortalAccount)
// since vendor-portal routing doesn't need to resolve an account before
// deciding to mount the app -- that happens inside VendorPortalApp itself,
// off the accountId segment this pattern matches but doesn't otherwise use.
const VENDOR_PORTAL_ROUTE_PATTERN = /^\/(?:[^/]+\/)?vendor-portal(\/|$)/

const useIsVendorPortalRoute = (): boolean => {
  const { pathname } = useLocation()
  return VENDOR_PORTAL_ROUTE_PATTERN.test(pathname)
}

const VendorPortal = () => {
  const VendorPortalApp = useMemo(
    () => lazy(() => import('./VendorPortalApp')),
    [],
  )

  return (
    <Suspense fallback={<></>}>
      <VendorPortalApp />
    </Suspense>
  )
}

// VENDOR_ACCOUNT_CHOOSER_ROUTE_PATTERN matches the dedicated account-chooser
// page's bare path, /vendor-account-chooser -- see ntrced account.go's
// BuildVendorAccountChooserLink, which is the only place this URL is
// generated (an invite email link, not something a user navigates to
// directly). No account-id segment, unlike the vendor portal's
// VENDOR_PORTAL_ROUTE_PATTERN, since which account this resolves to is
// exactly the question this page answers.
const VENDOR_ACCOUNT_CHOOSER_ROUTE_PATTERN = /^\/vendor-account-chooser(\/|$)/

const useIsVendorAccountChooserRoute = (): boolean => {
  const { pathname } = useLocation()
  return VENDOR_ACCOUNT_CHOOSER_ROUTE_PATTERN.test(pathname)
}

const VendorAccountChooser = () => {
  const VendorAccountChooserApp = useMemo(
    () => lazy(() => import('./VendorAccountChooserApp')),
    [],
  )

  return (
    <Suspense fallback={<></>}>
      <VendorAccountChooserApp />
    </Suspense>
  )
}

const AppChooser = () => {
  const AppAuth = useMemo(() => lazy(() => import('./AppAuth')), [])

  const { accountId, isAuthenticated } = useAuth()
  const hasFreshAccessLinkToken = useHasFreshAccessLinkToken()

  // A fresh access/invite link's `?token=` must win even if a *different*
  // session is already active on this device (e.g. clicking a second
  // vendor's invite link while still signed in as the first vendor) --
  // otherwise this URL falls straight through to <AppAuth> with the old
  // session's accountId and the new token is silently dropped. Forcing
  // <AppUnauth> here mounts the same AttemptUrlLogin (see
  // pages/Login/AttemptLogin.tsx) that handles a normal first-time
  // access-link visit, so it replaces AuthContext's session exactly as it
  // would for a brand-new login -- no separate "switch account" path needed.
  if (!isAuthenticated || !accountId || hasFreshAccessLinkToken) {
    return <AppUnauth />
  }

  return (
    <Suspense fallback={<></>}>
      <AppAuth accountId={accountId} />
    </Suspense>
  )
}

// Access/invite links always land on /login?token=... (see ntrced's
// ui_post_signin_url config), which is also the one AppUnauth route
// (LoginPage) that mounts AttemptUrlLogin. Scoped to that path (rather than
// firing on any `token` param anywhere) so this can't misfire on an
// unrelated authenticated page that happens to carry a same-named query
// param.
const useHasFreshAccessLinkToken = (): boolean => {
  const { pathname, search } = useLocation()
  return useMemo(
    () =>
      pathname.startsWith('/login') && Boolean(queryString.parse(search).token),
    [pathname, search],
  )
}
